/**
 * Trading Analysis Context
 *
 * Provides computed analytics data to all child components.
 * Fetches fills from the portfolio context and computes all analytics.
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type {
  CompletedTrade,
  AdvancedStats,
  TimeAnalysis,
  AssetAnalysis,
  BehaviorAnalysis,
  TraderProfile,
  Achievement,
} from "@nordex/core";
import type { Fill } from "@/backend/hooks/use-trade-history";
import {
  useCompletedTrades,
  filterTradesByDateRange,
  useAdvancedStats,
  useTimeAnalysis,
  useAssetAnalysis,
  useBehaviorAnalysis,
  useAchievements,
  useTraderProfile,
} from "./hooks";
import { type DateRangeValue } from "@/components/ui/date-range-selector";

interface TradingAnalysisContextValue {
  // Raw data
  fills: Fill[];
  isLoading: boolean;

  // Computed data
  trades: CompletedTrade[];
  filteredTrades: CompletedTrade[];
  stats: AdvancedStats;
  timeAnalysis: TimeAnalysis;
  assetAnalysis: AssetAnalysis;
  behavior: BehaviorAnalysis;
  achievements: Achievement[];
  unlockedAchievements: number;
  totalAchievements: number;
  profiles: TraderProfile[];
  primaryProfile: TraderProfile | null;

  // Date range
  dateRange: DateRangeValue;
}

const TradingAnalysisContext = createContext<TradingAnalysisContextValue | null>(
  null
);

interface TradingAnalysisProviderProps {
  children: ReactNode;
  fills: Fill[];
  isLoading: boolean;
  dateRange: DateRangeValue;
}

export function TradingAnalysisProvider({
  children,
  fills,
  isLoading,
  dateRange,
}: TradingAnalysisProviderProps) {
  // Compute completed trades from fills
  const { trades } = useCompletedTrades(fills);

  // Filter trades by date range
  const filteredTrades = useMemo(
    () => filterTradesByDateRange(trades, dateRange),
    [trades, dateRange]
  );

  // Calculate all analytics from filtered trades
  const stats = useAdvancedStats(filteredTrades);
  const timeAnalysis = useTimeAnalysis(filteredTrades);
  const assetAnalysis = useAssetAnalysis(filteredTrades);
  const behavior = useBehaviorAnalysis(filteredTrades);

  // Calculate all-time stats for achievements and profiles (not affected by time filter)
  const allTimeStats = useAdvancedStats(trades);
  const allTimeTimeAnalysis = useTimeAnalysis(trades);
  const allTimeAssetAnalysis = useAssetAnalysis(trades);
  const allTimeBehavior = useBehaviorAnalysis(trades);

  // Calculate achievements from all-time trades (not filtered)
  const {
    achievements,
    unlockedCount: unlockedAchievements,
    totalCount: totalAchievements,
  } = useAchievements(trades, allTimeStats);

  // Determine trader profiles from all-time data (so they don't disappear on short intervals)
  const { profiles, primaryProfile } = useTraderProfile(
    trades,
    allTimeStats,
    allTimeTimeAnalysis,
    allTimeAssetAnalysis,
    allTimeBehavior
  );

  const value = useMemo<TradingAnalysisContextValue>(
    () => ({
      fills,
      isLoading,
      trades,
      filteredTrades,
      stats,
      timeAnalysis,
      assetAnalysis,
      behavior,
      achievements,
      unlockedAchievements,
      totalAchievements,
      profiles,
      primaryProfile,
      dateRange,
    }),
    [
      fills,
      isLoading,
      trades,
      filteredTrades,
      stats,
      timeAnalysis,
      assetAnalysis,
      behavior,
      achievements,
      unlockedAchievements,
      totalAchievements,
      profiles,
      primaryProfile,
      dateRange,
    ]
  );

  return (
    <TradingAnalysisContext.Provider value={value}>
      {children}
    </TradingAnalysisContext.Provider>
  );
}

export function useTradingAnalysis(): TradingAnalysisContextValue {
  const context = useContext(TradingAnalysisContext);
  if (!context) {
    throw new Error(
      "useTradingAnalysis must be used within a TradingAnalysisProvider"
    );
  }
  return context;
}

